/**
 * Hilo de comentarios, menciones y adjuntos.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import TrashIcon from '../../assets/icons/trash.svg';
import ReplyIcon from '../../assets/icons/reply.svg';
import SendIcon from '../../assets/icons/send.svg';
import ClipboardListIcon from '../../assets/icons/clipboard-list.svg';
import { MentionsInput, Mention } from 'react-mentions';
import { storage } from '../../firebaseConfig';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import './ReportComments.css';
import { ink, studio, FilterPill } from './studioUi';
import { useAuth } from '../../AuthContext';
import { useIsNarrow, VH } from '../../useMediaQuery';

// Componente para mostrar un comentario citado (la respuesta)
const QuotedComment = ({ comment, styles, formatTimestamp }) => {
    if (!comment) return null;

    const truncatedText = comment.text && comment.text.length > 100 ? comment.text.substring(0, 100) + '...' : comment.text;
    const displayText = truncatedText || (comment.attachmentFilename ? `Archivo: ${comment.attachmentFilename}` : 'Comentario sin texto');

    return (
        <div style={styles.commentQuoteBlock}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ margin: 0, fontWeight: 'bold' }}>
                    Respondiendo a {comment.authorInfo?.name || 'Usuario desconocido'}
                </p>
                <span style={{ fontSize: '0.8rem', color: '#5f6368', fontWeight: 'normal' }}>
                    {formatTimestamp(comment.timestamp)}
                </span>
            </div>
            <blockquote style={{ margin: '4px 0 0 0', padding: 0, fontStyle: 'italic' }}>
                "{displayText}"
            </blockquote>
        </div>
    );
};

const MENTION_TOKEN = /@\[([^\]]+)\](?:\(([^)]+)\))?/g;

const renderCommentWithMentions = (comment) => {
    const displayText = comment.text || '';
    if (!displayText) return '';

    const elements = [];
    let lastIndex = 0;
    const regex = new RegExp(MENTION_TOKEN.source, 'g');
    let match;
    let key = 0;

    while ((match = regex.exec(displayText)) !== null) {
        if (match.index > lastIndex) {
            elements.push(displayText.slice(lastIndex, match.index));
        }
        elements.push(
            <span key={`m-${key++}`} style={{ color: '#5E35B1', fontWeight: 'bold' }}>
                @{match[1]}
            </span>
        );
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < displayText.length) {
        elements.push(displayText.slice(lastIndex));
    }
    return elements;
};

const resolveMentionIds = (text, pending, mentionData) => {
    const ids = new Set();
    const regex = new RegExp(MENTION_TOKEN.source, 'g');
    let match;
    while ((match = regex.exec(text || '')) !== null) {
        const name = match[1];
        const markupId = match[2];
        if (markupId && !/\s/.test(markupId)) {
            ids.add(markupId);
        } else {
            const hit = mentionData.find((u) => u.display === name);
            if (hit?.id) ids.add(hit.id);
        }
    }
    (pending || []).forEach((mention) => {
        if (mention.id && !/\s/.test(mention.id)) {
            ids.add(mention.id);
        } else if (mention.display) {
            const hit = mentionData.find((u) => u.display === mention.display);
            if (hit?.id) ids.add(hit.id);
        }
    });
    return [...ids];
};

const ReportComments = ({ reportId, currentUser, styles, fetchWithAuth, API_URL_INTERNAL, userPermissions, commentsLocked, onUpdateReportProperties, commentRefreshKey, onAdjustmentCreated, reportResponsibles = [], active = true }) => {
    const { withBusy } = useAuth();
    const narrow = useIsNarrow();
    const listRef = useRef(null);
    const [comments, setComments] = useState([]);
    const [commentsMap, setCommentsMap] = useState(new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newCommentText, setNewCommentText] = useState('');
    const [pendingMentions, setPendingMentions] = useState([]);
    const [replyingTo, setReplyingTo] = useState(null);
    const [attachment, setAttachment] = useState(null); // { file, previewUrl, name }
    const [isUploading, setIsUploading] = useState(false);
    const [allUsers, setAllUsers] = useState([]); // NEW: Local state for mentionable users
    const [adjustmentAssigneeId, setAdjustmentAssigneeId] = useState('');
    const commentsEndRef = useRef(null);

    const assigneeOptions = useMemo(() => {
        const seen = new Set();
        return (reportResponsibles || []).filter((row) => {
            if (!row.userId || seen.has(row.userId)) return false;
            seen.add(row.userId);
            return true;
        });
    }, [reportResponsibles]);

    const mentionData = useMemo(() => (allUsers || [])
        .filter(user => user && typeof user.nombre === 'string' && user.nombre.trim() !== '')
        .map(user => ({
            id: user.uid,
            display: user.nombre,
        })), [allUsers]);

    /* Baja solo dentro de la lista; scrollIntoView movía también la página completa. */
    const scrollToBottom = () => {
        const list = listRef.current;
        if (list) list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
    };

    const fetchComments = async () => {
        setLoading(true);
        try {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/reportes-maestros/${reportId}/comments`);
            const data = await res.json();
            if (data.success) {
                setComments(data.comments);
                const map = new Map();
                data.comments.forEach(c => map.set(c.id, c));
                setCommentsMap(map);
            } else {
                throw new Error(data.error || 'Failed to fetch comments');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (reportId) {
            fetchComments();

            // NEW: Fetch users for mentions when the component mounts with a reportId
            const fetchMentionUsers = async () => {
                try {
                    const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/users/for-mention`);
                    const data = await res.json();
                    if (data.success) {
                        setAllUsers(data.users || []);
                    }
                } catch (err) {
                    console.error("Failed to fetch users for mention:", err);
                }
            };
            fetchMentionUsers();

        }
    }, [reportId, commentRefreshKey, fetchWithAuth, API_URL_INTERNAL]);

    useEffect(() => {
        if (!loading && active) {
            scrollToBottom();
        }
    }, [comments, loading, active]);

    const handlePostComment = async () => {
        const textToPost = newCommentText.trim();
        if (!textToPost && !attachment) return;
        if (isUploading) return;

        await withBusy(async () => {
        setIsUploading(true);

        let attachmentPayload = {};
        if (attachment && attachment.file) {
            try {
                const timestamp = Date.now();
                const fileExtension = attachment.file.name.split('.').pop();
                const uniqueFilename = `${currentUser.uid}_${timestamp}.${fileExtension}`;
                const imageRef = storageRef(storage, `report_comments/${reportId}/${uniqueFilename}`);
                
                const options = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };
                const compressedFile = await imageCompression(attachment.file, options);

                const snapshot = await uploadBytes(imageRef, compressedFile);
                const downloadURL = await getDownloadURL(snapshot.ref);

                attachmentPayload = {
                    attachmentUrl: downloadURL,
                    attachmentType: compressedFile.type,
                    attachmentFilename: attachment.file.name
                };
            } catch (uploadError) {
                console.error("Error uploading attachment:", uploadError);
                alert("Error al subir el archivo adjunto. El comentario no fue publicado.");
                setIsUploading(false);
                return;
            }
        }

        const mentionedUserIds = resolveMentionIds(textToPost, pendingMentions, mentionData);

        const payload = {
            text: textToPost,
            parentId: replyingTo ? replyingTo.id : null,
            mentionedUserIds,
            ...attachmentPayload
        };

        try {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/reportes-maestros/${reportId}/comments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                setNewCommentText('');
                setPendingMentions([]);
                setReplyingTo(null);
                setAttachment(null);
                await fetchComments();
            } else {
                const errorData = await res.json();
                alert(`Error al publicar comentario: ${errorData.error}`);
            }
        } catch (err) {
            alert(`Error de conexión: ${err.message}`);
        } finally {
            setIsUploading(false);
        }
        }, attachment ? 'Subiendo y publicando…' : 'Publicando comentario…');
    };

    const createAdjustment = async (payload) => {
        if (isUploading) return;
        if (!adjustmentAssigneeId) {
            alert('Asigna el ajuste a un responsable del reporte.');
            return;
        }
        await withBusy(async () => {
        setIsUploading(true);
        try {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/reportes-maestros/${reportId}/adjustments`, {
                method: 'POST',
                body: JSON.stringify({ ...payload, assignedToUserId: adjustmentAssigneeId }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'No se pudo crear el ajuste.');
            if (onAdjustmentCreated) onAdjustmentCreated();
        } catch (err) {
            alert(err.message || 'No se pudo crear el ajuste.');
        } finally {
            setIsUploading(false);
        }
        }, 'Creando ajuste…');
    };

    const handlePostAsAdjustment = async () => {
        const textToPost = newCommentText.trim();
        if (!textToPost) return;
        await createAdjustment({ text: textToPost });
        setNewCommentText('');
        setPendingMentions([]);
    };

    const handleConvertComment = (comment) => {
        if (!comment?.id) return;
        createAdjustment({ sourceCommentId: comment.id });
    };

    const handleDeleteComment = async (commentId) => {
        if (window.confirm('¿Estás seguro de que quieres eliminar este comentario y todas sus posibles respuestas?')) {
            await withBusy(async () => {
            try {
                const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/reportes-maestros/${reportId}/comments/${commentId}`, {
                    method: 'DELETE'
                });
                if (res.ok) {
                    await fetchComments();
                } else {
                    const errorData = await res.json();
                    alert(`Error al eliminar: ${errorData.error}`);
                }
            } catch (err) {
                alert(`Error de conexión: ${err.message}`);
            }
            }, 'Eliminando comentario…');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';

        let date;
        // Check if it's a Firestore Timestamp object (from a direct SDK call)
        if (timestamp.toDate && typeof timestamp.toDate === 'function') {
            date = timestamp.toDate();
        } 
        // Check if it's a serialized timestamp from a JSON response
        else if (timestamp._seconds !== undefined && timestamp._nanoseconds !== undefined) {
            date = new Date(timestamp._seconds * 1000);
        } 
        // Fallback for other potential date string formats
        else {
            date = new Date(timestamp);
        }

        // Ensure the created date is valid before trying to format it
        if (isNaN(date.getTime())) return '';

        return date.toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const handlePaste = (e) => {
        if (isUploading || attachment) return;

        const items = e.clipboardData.items;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                const file = items[i].getAsFile();
                if (file) {
                    e.preventDefault();
                    const previewUrl = URL.createObjectURL(file);
                    const newName = `pasted_image_${Date.now()}.${file.type.split('/')[1] || 'png'}`;
                    // Create a new File object with the new name, as the original from clipboard is read-only
                    const newFile = new File([file], newName, {
                        type: file.type,
                        lastModified: file.lastModified,
                    });
                    setAttachment({ file: newFile, previewUrl, name: newName });
                    break;
                }
            }
        }
    };

    return (
        <div style={{ ...studio.card, ...(narrow ? { padding: '14px 14px', height: `min(84${VH}, 720px)` } : { height: 550 }), display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 12, marginBottom: 12, borderBottom: `1px solid ${ink.line}`, flexShrink: 0 }}>
                <div>
                    <p style={studio.kicker}>Chat</p>
                    <h4 style={{ ...studio.formTitle, margin: '4px 0 0' }}>Comentarios</h4>
                </div>
                {commentsLocked && (
                    <span style={{ ...studio.hint, color: ink.danger, fontWeight: 700 }}>Cerrado</span>
                )}
            </div>

            <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: narrow ? '0 2px 10px 0' : '0 10px 10px 0', marginBottom: '15px' }}>
                {loading && <p style={studio.hint}>Cargando comentarios…</p>}
                {error && <p style={{ color: ink.danger }}>Error: {error}</p>}
                {!loading && comments.length === 0 && <p style={studio.hint}>No hay comentarios todavía. Sé el primero.</p>}

                {comments.map(comment => {
                    const isMyComment = currentUser.uid === comment.authorInfo?.uid;
                    const bubbleStyle = isMyComment ? { ...styles.commentBubble, ...styles.myCommentBubble } : styles.commentBubble;

                    return (
                        <div key={comment.id} style={{ marginBottom: '20px' }}>
                            {comment.parentId && <QuotedComment comment={commentsMap.get(comment.parentId)} styles={styles} formatTimestamp={formatTimestamp} />}
                            
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                <img
                                    src={comment.authorInfo?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.authorInfo?.name || 'N/A')}&background=5E35B1&color=fff&size=35`}
                                    alt="Perfil"
                                    style={{ ...styles.miniProfileImage, width: '35px', height: '35px' }}
                                    referrerPolicy="no-referrer"
                                />
                                <div style={{ flex: 1 }}>
                                    <div style={bubbleStyle}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: narrow ? 'wrap' : 'nowrap', gap: narrow ? 6 : 0 }}>
                                            <div style={{ minWidth: 0 }}>
                                                <span style={{ fontWeight: 'bold' }}>{comment.authorInfo?.name || 'Usuario desconocido'}</span>
                                                <span style={{ color: '#5f6368', fontSize: '0.8rem', marginLeft: '10px' }}>
                                                    {formatTimestamp(comment.timestamp)}
                                                </span>
                                            </div>
                                            
                                            <div style={{ display: 'flex', gap: narrow ? '8px' : '15px', alignItems: 'center', marginLeft: 'auto' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleConvertComment(comment)}
                                                    title="Convertir en ajuste"
                                                    disabled={isUploading || !adjustmentAssigneeId}
                                                    style={{ ...styles.sBtn, width: narrow ? '36px' : '28px', height: narrow ? '36px' : '28px' }}
                                                >
                                                    <img src={ClipboardListIcon} alt="Ajuste" style={{ width: '15px', height: '15px' }} />
                                                </button>
                                                {!commentsLocked && (
                                                    <button onClick={() => setReplyingTo(comment)} title="Responder" style={{...styles.sBtn, width: narrow ? '36px' : '28px', height: narrow ? '36px' : '28px'}}>
                                                        <img src={ReplyIcon} alt="Responder" style={{ width: '15px', height: '15px' }} />
                                                    </button>
                                                )}
                                                {userPermissions.reports_manage_comments && (
                                                    <button onClick={() => handleDeleteComment(comment.id)} style={{...styles.sBtnR, width: narrow ? '36px' : '28px', height: narrow ? '36px' : '28px'}}>
                                                        <img src={TrashIcon} alt="Eliminar" style={{ width: '15px', height: '15px' }} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <p style={{ margin: '8px 0 0 0', whiteSpace: 'pre-wrap', color: '#050505', fontSize: '0.95rem', lineHeight: '1.5' }}>
                                            {renderCommentWithMentions(comment)}
                                        </p>
                                        {comment.attachmentUrl && (
                                            <div style={{ marginTop: '10px' }}>
                                                {comment.attachmentType && comment.attachmentType.startsWith('image/') ? (
                                                    <a href={comment.attachmentUrl} target="_blank" rel="noopener noreferrer">
                                                        <img src={comment.attachmentUrl} alt={comment.attachmentFilename || 'Adjunto'} style={{ maxWidth: narrow ? '100%' : '300px', maxHeight: '300px', borderRadius: '8px', border: '1px solid #eee' }} referrerPolicy="no-referrer" />
                                                    </a>
                                                ) : (
                                                    <a href={comment.attachmentUrl} target="_blank" rel="noopener noreferrer" className="attachment-link">{comment.attachmentFilename || 'Descargar archivo adjunto'}</a>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div ref={commentsEndRef} />
            </div>

            {!commentsLocked && (
                <div style={{ marginTop: 'auto', flexShrink: 0 }}>
                    {replyingTo && (
                        <div style={{ fontSize: '0.85rem', padding: '5px 10px', backgroundColor: '#e8f0fe', borderRadius: '4px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Respondiendo a <strong>{replyingTo.authorInfo.name}</strong></span>
                            <button onClick={() => setReplyingTo(null)} aria-label="Cancelar respuesta" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: narrow ? '1.4rem' : '1rem', color: '#5f6368', minWidth: narrow ? 36 : undefined, minHeight: narrow ? 36 : undefined }}>&times;</button>
                        </div>
                    )}
                    {attachment && (
                        <div className="attachment-preview-container">
                            <img src={attachment.previewUrl} alt="Vista previa" className="attachment-preview-image" />
                            <span className="attachment-name">{attachment.name}</span>
                            <button onClick={() => setAttachment(null)} className="remove-attachment-button" disabled={isUploading}>&times;</button>
                        </div>
                    )}
                    <div style={{ position: 'relative' }}>
                        <MentionsInput
                            value={newCommentText}
                            onChange={(event, newValue, newPlainTextValue, mentions) => {
                                setNewCommentText(newValue);
                                setPendingMentions(mentions || []);
                            }}
                            placeholder="Escribe un comentario... Menciona a alguien con @"
                            className="mentions-input"
                            allowSuggestionsAboveCursor
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePostComment(); } }}
                            onPaste={handlePaste}
                            a11ySuggestionsListLabel={"Sugerencias de usuarios para mencionar"}
                        >
                            <Mention
                                trigger="@"
                                data={mentionData}
                                markup="@[__display__](__id__)"
                                className="mentions-input__mention"
                                appendSpaceOnAdd
                                allowSpaceInQuery
                                renderSuggestion={(suggestion, search, highlightedDisplay, index, focused) => {
                                    const user = allUsers.find(u => u.uid === suggestion.id);
                                    const photoURL = user?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.nombre || 'N/A')}&background=5E35B1&color=fff&size=30`;
                                    return (
                                        <div className={`user-suggestion ${focused ? 'focused' : ''}`}>
                                            <img
                                                src={photoURL}
                                                alt={suggestion.display}
                                                className="mention-suggestion-avatar"
                                                referrerPolicy="no-referrer"
                                            />
                                            {highlightedDisplay}
                                        </div>
                                    );
                                }}
                                displayTransform={(id, display) => `@${display}`}
                            />
                        </MentionsInput>
                        <button onClick={handlePostComment} style={{...styles.sBtn, position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', backgroundColor: '#5E35B1' }} disabled={isUploading}>
                            <img src={SendIcon} alt="Enviar" style={{ width: '20px', height: '20px', filter: 'invert(1)' }} />
                        </button>
                    </div>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8, maxWidth: narrow ? '100%' : 320 }}>
                        <span style={{ fontSize: '0.75rem', color: ink.muted, fontWeight: 600 }}>Asignar ajuste a</span>
                        <FilterPill
                            value={adjustmentAssigneeId}
                            onChange={setAdjustmentAssigneeId}
                            emptyLabel={assigneeOptions.length ? 'Elige un responsable' : 'Sin responsables'}
                            style={{ minWidth: narrow ? 0 : 240, width: '100%', ...(narrow ? { fontSize: 16, minHeight: 40 } : null) }}
                        >
                            {assigneeOptions.map((row) => (
                                <option key={row.userId} value={row.userId}>
                                    {(row.userDetails?.nombre || row.nombre || 'Responsable') + (row.area ? ` · ${row.area}` : '')}
                                </option>
                            ))}
                        </FilterPill>
                    </label>
                    <button
                        type="button"
                        onClick={handlePostAsAdjustment}
                        disabled={isUploading || !newCommentText.trim() || !adjustmentAssigneeId}
                        title="Enviar como ajuste"
                        style={{
                            ...studio.ghostBtn,
                            marginTop: 8,
                            padding: '6px 10px',
                            fontSize: '0.78rem',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                        }}
                    >
                        <img src={ClipboardListIcon} alt="" style={{ width: 14, height: 14 }} />
                        Enviar como ajuste
                    </button>
                </div>
            )}
            {commentsLocked && (
                <div style={{ ...studio.empty, padding: 16 }}>
                    Los comentarios para este reporte han sido cerrados.
                </div>
            )}
        </div>
    );
};

export default ReportComments;